import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, Loader2, Mail, Phone, RotateCcw, User, XCircle, PackageCheck } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { cancelReservation, extendReservation, getAdminReservations, markReservationSold, markReservationReady } from '../../config';
import { useAuth } from '../../context/AuthContext';

const STATUS_CONFIG = {
  active: { label: 'Active', cls: 'bg-amber-100 text-amber-600' },
  sold: { label: 'Vendue', cls: 'bg-emerald-50 text-emerald-600' },
  cancelled: { label: 'Annulee', cls: 'bg-rose-100 text-rose-600' },
  expired: { label: 'Expiree', cls: 'bg-gray-500/20 text-slate-600' },
};

// Miroir de `services/cart_svc.py` (`cart_count_label`, `cart_items_label`) : cet
// écran lit le DOCUMENT DE RÉSERVATION, pas la commande dérivée, et n'a donc aucun
// libellé serveur sous la main. Le scalaire `product_name` ne porte que la première
// ligne alors que le montant affiché couvre tout le panier : sans ce compteur, la
// fiche annonce « PC A » avec un badge « Payée 800,00 € ».
function cartUnits(items) {
  return (items || []).reduce(
    (total, line) => total + Math.max(1, Number(line?.qty) || 1),
    0,
  );
}

function cartItemsLabel(items) {
  return (items || []).map((line) => {
    const name = line?.product_name || line?.product_id || 'article';
    const qty = Math.max(1, Number(line?.qty) || 1);
    return qty > 1 ? `${name} × ${qty}` : String(name);
  }).join(', ');
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export default function AdminReservations() {
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission('reservations:update');
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  // Refus serveur de la dernière prolongation, rattaché à SA réservation : affiché
  // sous le bouton, jamais dans une alerte navigateur.
  const [actionError, setActionError] = useState({ id: '', message: '' });

  const fetchReservations = async () => {
    try {
      const data = await getAdminReservations(statusFilter);
      setReservations(data.reservations || []);
    } catch {
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [statusFilter]);

  const handleSold = async (id) => {
    if (!canUpdate) return;
    setActionLoading(id);
    try {
      await markReservationSold(id);
      await fetchReservations();
    } finally {
      setActionLoading('');
    }
  };

  const handleReady = async (id) => {
    if (!canUpdate) return;
    setActionLoading(id);
    try {
      await markReservationReady(id);
      await fetchReservations();
    } finally {
      setActionLoading('');
    }
  };

  const handleCancel = async (id) => {
    if (!canUpdate || !window.confirm('Annuler cette reservation ?')) return;
    setActionLoading(id);
    try {
      await cancelReservation(id);
      await fetchReservations();
    } finally {
      setActionLoading('');
    }
  };

  // Prolongation : seule action que l'API accepte aussi sur une réservation expirée
  // (`extend_reservation` admet `active` et `expired`, et repasse le document en
  // « active »). C'est le recours du client qui se présente après la fermeture.
  const handleExtend = async (id) => {
    if (!canUpdate) return;
    setActionLoading(id);
    setActionError({ id: '', message: '' });
    try {
      await extendReservation(id, 24);
      await fetchReservations();
    } catch (error) {
      // Message du serveur repris tel quel : lui seul dit POURQUOI le refus (409 si
      // le statut est devenu inéligible entre l'affichage et le clic, 404 si la
      // réservation a disparu, 401/403 si les droits ne suivent plus).
      // Pas de relecture de la liste ici : elle pourrait remplacer la ligne et
      // emporter le refus avec elle avant que le gestionnaire l'ait lu.
      setActionError({ id, message: error?.message || 'La prolongation a échoué.' });
    } finally {
      setActionLoading('');
    }
  };

  return (
    <AdminLayout title="Reservations">
      <div className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          {[
            { value: '', label: 'Toutes' },
            { value: 'active', label: 'Actives' },
            { value: 'sold', label: 'Vendues' },
            { value: 'cancelled', label: 'Annulees' },
            { value: 'expired', label: 'Expirees' },
          ].map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setStatusFilter(filter.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                statusFilter === filter.value
                  ? 'bg-secondary/10 text-secondary border border-secondary/30'
                  : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-secondary animate-spin" />
          </div>
        ) : reservations.length === 0 ? (
          <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
            <p className="text-slate-500">Aucune reservation</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reservations.map((reservation) => {
              const status = STATUS_CONFIG[reservation.status] || STATUS_CONFIG.active;
              const isLoading = actionLoading === reservation.id;
              const units = cartUnits(reservation.items);
              return (
                <div key={reservation.id} className="bg-white rounded-sm border border-slate-200 p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800 text-sm">{reservation.product_name}</span>
                        {units > 1 && (
                          <span
                            data-testid={`reservation-items-count-${reservation.id}`}
                            title={cartItemsLabel(reservation.items)}
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
                          >
                            {units} articles
                          </span>
                        )}
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${status.cls}`}>{status.label}</span>
                        {reservation.status === 'active' && (reservation.pickup_ready
                          ? <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">Prête à retirer</span>
                          : <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">En attente d'arrivée</span>)}
                        {reservation.order_type === 'special_order' && (
                          <>
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">Commande</span>
                            {reservation.deposit_status === 'paid'
                              ? <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Payée {Number(reservation.deposit_amount || 0).toFixed(2)} €</span>
                              : <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-600">Paiement en attente</span>}
                          </>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-1.5 text-slate-700">
                          <User className="w-3.5 h-3.5 text-slate-500" />
                          {reservation.first_name} {reservation.last_name}
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-700">
                          <Phone className="w-3.5 h-3.5 text-slate-500" />
                          <a href={`tel:${reservation.phone}`} className="hover:text-secondary">{reservation.phone}</a>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-700">
                          <Mail className="w-3.5 h-3.5 text-slate-500" />
                          <a href={`mailto:${reservation.email}`} className="hover:text-secondary">{reservation.email}</a>
                        </div>
                      </div>

                      <div className="flex gap-4 text-xs text-slate-500">
                        <span>Creee : {formatDate(reservation.created_at)}</span>
                        {reservation.expires_at && <span>Expire : {formatDate(reservation.expires_at)}</span>}
                      </div>
                    </div>

                    {canUpdate && (reservation.status === 'active' || reservation.status === 'expired') && (
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {reservation.status === 'active' && (
                          <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                            {!reservation.pickup_ready && (
                              <button
                                type="button"
                                onClick={() => handleReady(reservation.id)}
                                disabled={isLoading}
                                className="inline-flex items-center gap-1.5 bg-secondary/10 text-secondary border border-secondary/30 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-secondary/20 transition-colors disabled:opacity-50"
                              >
                                {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageCheck className="w-3 h-3" />}
                                Marquer prêt
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleSold(reservation.id)}
                              disabled={isLoading}
                              className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-600 border border-green-500/20 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-emerald-50 transition-colors disabled:opacity-50"
                            >
                              {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                              Vendue
                            </button>
                            <button
                              type="button"
                              onClick={() => handleExtend(reservation.id)}
                              disabled={isLoading}
                              className="inline-flex items-center gap-1.5 bg-secondary/5 text-secondary border border-blue-500/20 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-secondary/15 transition-colors disabled:opacity-50"
                            >
                              <Clock className="w-3 h-3" />
                              +24h
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCancel(reservation.id)}
                              disabled={isLoading}
                              className="inline-flex items-center gap-1.5 bg-rose-50 text-rose-600 border border-rose-200 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-rose-100 transition-colors disabled:opacity-50"
                            >
                              <XCircle className="w-3 h-3" />
                              Annuler
                            </button>
                          </div>
                        )}

                        {/* Expirée : SEULE la prolongation est offerte. « Vendue » et
                            « Marquer prêt » exigent `status === "active"` côté API — les
                            afficher ici ne produirait que des refus 409. */}
                        {reservation.status === 'expired' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleExtend(reservation.id)}
                              disabled={isLoading}
                              data-testid={`reservation-reactivate-${reservation.id}`}
                              className="inline-flex items-center gap-1.5 bg-secondary/5 text-secondary border border-blue-500/20 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-secondary/15 transition-colors disabled:opacity-50"
                            >
                              {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                              Réactiver +24h
                            </button>
                            <p
                              data-testid={`reservation-reactivate-hint-${reservation.id}`}
                              className="text-[11px] leading-snug text-slate-500 text-right max-w-xs"
                            >
                              Remet la réservation en « active » pour 24 h et reprend l’article s’il est
                              encore en réserve. Si un autre client l’a réservé entre-temps, il n’est pas
                              repris : vérifiez le stock avant d’annoncer le retrait.
                            </p>
                          </>
                        )}

                        {actionError.id === reservation.id && actionError.message && (
                          <p
                            data-testid={`reservation-action-error-${reservation.id}`}
                            className="text-[11px] leading-snug text-rose-600 text-right max-w-xs"
                          >
                            {actionError.message}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
